import ActiveConfig from './activeConfig'
import PsmEventSender from './psm-event-sender'
import PsmEventQueue from './psm-event-queue'
import PsmBridge from './psm-bridge'
import PsmTimer from './psm-timer'
import {
  IEventBuilder, IEventSender, ITimer, ObjectMap, ITopVideoError,
} from './interfaces'
import {
  deepmerge, mapMetadata,
} from "../utils";
import { generateUUID } from "../utils/uuid";
import { PlayerMediaStates } from './enums'
import { PlayerState, PayloadType, CurrentEventType } from '../utils/dataTypes'
import { CLEAN_STREAMTYPE_CHECK_EVENTS } from './constants'


// TODO: add unit test(s)
/**
 * Creates a new Event object when you user starts video
 * and re-use sessionID during all events. But videoSessionId will change
 * based on the timestamp.
 *
 */
class PsmEventBuilder implements IEventBuilder {
  videoSessionId: string
  videoSessionStartTimestamp: string
  eventCount: number = 0
  eventSender: IEventSender
  heartbeatTimer: ITimer
  currentVersion: string
  playerState: Partial<PlayerState> = { video: {} }
  isAdPaused: boolean = false
  bridge: PsmBridge

  constructor(bridge: PsmBridge) {
    this.bridge = bridge
    this.eventSender = new PsmEventSender(this)
    const heartbeatIntervalSeconds = ActiveConfig.activeConfig?.heartbeatIntervalSeconds
    const heartbeatEventCallback = () => this.eventSender.sendEvents()
    this.heartbeatTimer = new PsmTimer(heartbeatEventCallback, heartbeatIntervalSeconds)
    this.currentVersion = ActiveConfig.currentVersion
    this.videoSessionId = generateUUID()
    this.videoSessionStartTimestamp = new Date().toISOString()
  }

  getVideoStateAttribute(type: string): any {
    return this.playerState.video[type]
  }

  setVideoStateAttributes(type: string, data: any): void {
    this.playerState.video[type] = data
  }

  clearVideoSession() {
    this.videoSessionId = null
    this.videoSessionStartTimestamp = null
  }

  setDeviceMapping(payload: Partial<CurrentEventType>): Partial<PayloadType> {
    // take payload from bridge, and map to the values in the schema
    const mappedMetadata = mapMetadata(payload, ActiveConfig?.activeConfig?.psmDeviceMap?.map)
    return mappedMetadata
  }

  generateVideoSession() {
    this.videoSessionId = generateUUID()
    this.videoSessionStartTimestamp = new Date().toISOString()
  }

  queuePsmVidEvent(eventName: string) {
    if (PsmBridge.isThrottlingRequested) return

    let currentPayload: Partial<CurrentEventType> = {}

    if (this.currentVersion !== ActiveConfig.currentVersion) {
      const newInterval = ActiveConfig.activeConfig?.heartbeatIntervalSeconds
      if (this.heartbeatTimer.interval !== newInterval) {
        this.heartbeatTimer.updateInterval(newInterval)
      }
      this.currentVersion = ActiveConfig.currentVersion
    }

    if (!this.heartbeatTimer.isTimerRunning) {
      this.heartbeatTimer.start()
    }

    if (eventName === 'adPaused') {
      this.isAdPaused = true
    } else if (eventName === 'adResumed') {
      this.isAdPaused = false
    } else if (this.playerState.video.currentMediaState === PlayerMediaStates.PLAYING) {
      this.isAdPaused = false
    }

    if (!this.videoSessionId) {
      this.generateVideoSession()
    }

    // All events will have same properties
    currentPayload.videoSessionId = this.videoSessionId
    currentPayload.videoSessionStartTimestamp = this.videoSessionStartTimestamp
    currentPayload.eventName = eventName
    currentPayload.eventId = this.videoSessionId + '-' + ++this.eventCount
    currentPayload.eventType = 'VideoQos'
    currentPayload.eventTimestamp = new Date().toISOString()

    //  Those VIDEO DATA change between events or take longeer to load
    currentPayload.cdn = this.bridge.playerDependencies.getCDN()
    currentPayload.bitrate = this.playerState.video['bitrate']
    currentPayload.videoDurationSec = this.bridge.playerDependencies.getVideoDurationSec()
    currentPayload.streamHost = this.bridge.playerDependencies.getStreamHost()
    currentPayload.streamUrl = this.bridge.playerDependencies.getStreamUrl()
    currentPayload.autoplay = this.bridge.playerDependencies.getAutoplay()
    currentPayload.streamType = CLEAN_STREAMTYPE_CHECK_EVENTS.includes(eventName) ? null : this.bridge.playerDependencies.getStreamType()
    //TODO: Confirm the exact location for this value
    currentPayload.liveStreamName = currentPayload.streamType === 'Live' ? this.bridge.playerDependencies.getLiveStreamName() : null


    if (typeof currentPayload?.previousMediaState === 'undefined') {
      const previousMediaState = this.playerState.video.previousMediaState || this.playerState.video.currentMediaState || this.bridge.playerDependencies.currentMediaState() || null
      currentPayload.previousMediaState = previousMediaState
    }
    if (typeof currentPayload?.currentMediaState === 'undefined') {
      const currentMediaState = this.playerState.video.currentMediaState || this.bridge.playerDependencies.currentMediaState() || null
      currentPayload.currentMediaState = currentMediaState
    }
    if (typeof currentPayload?.previousVideoPosition === 'undefined' || this.playerState?.video?.previousVideoPosition === -1) {
      const previousVideoPosition = this.playerState.video.previousVideoPosition || this.bridge.playerDependencies.currentVideoPosition() || null
      currentPayload.previousVideoPosition = previousVideoPosition
    }
    if (typeof currentPayload?.currentVideoPosition === 'undefined' || this.playerState?.video?.currentVideoPosition === -1) {
      const currentVideoPosition = this.bridge.playerDependencies.currentVideoPosition() || null
      currentPayload.currentVideoPosition = currentVideoPosition
    }

    if (this.playerState?.error?.errorCode) {
      currentPayload.errorSeverity = this.playerState.error.errorSeverity
      currentPayload.errorCode = this.playerState.error.errorCode
      currentPayload.errorMessage = this.playerState.error.errorMessage
    }

    // Merge all Device specfic metadata to payload
    currentPayload = deepmerge(currentPayload, this.bridge.psmMetadata)

    // Map to device-map schema
    currentPayload = this.setDeviceMapping(currentPayload)
    // Merge in program-map schema data AFTER the deviceMapping
    if (this.bridge.mappedProgramMetadata) {
      currentPayload = deepmerge(currentPayload, this.bridge.mappedProgramMetadata)
    }


    PsmEventQueue.updateQueue(currentPayload)

    // don't call sendEvents for videoHeartbeat, because the videoHeartbeat event
    // is created by the sendEvents function
    let sendImmediately = ActiveConfig.activeConfig?.immediateEvents.includes(eventName)
    if (sendImmediately && eventName !== 'videoHeartbeat') {
      this.eventSender.sendEvents()
    }
  }

  getEventQueue = function (): ObjectMap {
    if (PsmEventQueue.queuedEvents.length === 0) {
      this.buildHeartbeatEvent()
    }
    const queueResults = PsmEventQueue.retrieveQueue()
    return queueResults
  }

  buildHeartbeatEvent = function () {
    const currentMediaState = this.getVideoStateAttribute('currentMediaState')
    if ((currentMediaState === PlayerMediaStates.PLAYING || currentMediaState === PlayerMediaStates.PENDING) && !this.isAdPaused) {
      this.queuePsmVidEvent('videoHeartbeat')
    }
  }

  setVideoError(error: ITopVideoError) {
    this.playerState.error = {
      errorSeverity: error?.error?._severity || null,
      errorCode: error?.error?._code || null,
      errorMessage: error?.error?._message || null,
    }
  }
}
export default PsmEventBuilder
